import React, { useEffect, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import Lottie from "lottie-react";
import { DASHBOARD_URL } from "../config";
import styles from "../styles/success.module.css";
import celebrationLottie from "../assets/lottie/celebration.json";
import handWithPhoneLottie from "../assets/lottie/hand-with-phone.json";

const APP_STORE_URL =
  "https://apps.apple.com/il/app/%D7%AA%D7%95%D7%A8-%D7%93%D7%99%D7%92%D7%99%D7%98%D7%9C-%D7%A0%D7%99%D7%94%D7%95%D7%9C-%D7%99%D7%95%D7%9E%D7%9F/id1602074493";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.adminappointmentapp";

// `live` = the end of the "עוד קצת וסיימנו" link: the page has just gone live
// (`updated`: it was live already). It isn't a new signup, so the signup
// conversion isn't counted again, and the link's session can't open the
// dashboard, so that goes through the normal login.
const Success = ({ live = false, updated = false }) => {
  const [isExploding] = useState(true);
  const [copied, setCopied] = useState(false);
  const [continuingToDashboard, setContinuingToDashboard] = useState(false);

  const businessCode = localStorage.getItem("business_code");
  const ownerName = localStorage.getItem("ownerName") || "";
  const authToken = localStorage.getItem("auth_token");
  const businessId = localStorage.getItem("business_id");
  const userType = localStorage.getItem("user_type") || "1";

  // Google Ads conversion event. Fires from the Success component
  // itself (not the OnboardingFlow effect) so every code path that
  // lands here is covered — including updateProfileCard's direct
  // navigate("/register/success") which used to bypass the push.
  // Guarded on authToken to avoid counting drive-by URL visits.
  useEffect(() => {
    if (!authToken || live) return;
    // Once per business: <Success /> mounts more than once on the way to
    // /register/success, and a refresh must not count the signup again
    const sentKey = `registration_success_sent_${businessId || businessCode || ""}`;
    try {
      if (localStorage.getItem(sentKey)) return;
      localStorage.setItem(sentKey, "1");
    } catch (_) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "registration_success",
        page_path: "/register/success",
      });
    } catch (_) {
      // Defensive — never let analytics block the user-facing flow.
    }
  }, [authToken, businessId, businessCode, live]);

  const pageUrl = `https://app.tor.digital/${businessCode}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const confettiOptions = {
    force: 0.85,
    duration: 4500,
    particleCount: 220,
    width: 1600,
  };

  const goToDashboard = () => {
    if (continuingToDashboard) return;
    setContinuingToDashboard(true);
    if (live) {
      window.location.href = `${DASHBOARD_URL}/auth/login`;
      return;
    }
    const params = new URLSearchParams({
      ssoToken: authToken || "",
      userId: businessId || "",
      userType,
    });
    window.location.href = `${DASHBOARD_URL}/auth/login?${params.toString()}`;
  };

  return (
    <div className={styles.container}>
      {isExploding && (
        <div className={styles.confettiAnchor}>
          <ConfettiExplosion {...confettiOptions} />
        </div>
      )}

      <div className={styles.card}>
        {/* ─── Hero ─── */}
        <div className={styles.hero}>
          <div className={styles.heroBadge}>
            <Lottie
              animationData={celebrationLottie}
              loop
              autoplay
              style={{ width: "100%", height: "100%" }}
            />
          </div>
          <h1 className={styles.heroTitle}>
            {ownerName ? `${ownerName}, ` : ""}
            {updated ? "הפרטים נשמרו! 🎉" : live ? "העמוד שלך באוויר! 🎉" : "העסק שלך מוכן! 🎉"}
          </h1>
          <p className={styles.heroSubtitle}>
            {updated
              ? "העמוד שלך מעודכן והלקוחות רואים אותו כבר עכשיו."
              : live
              ? "מהרגע הזה לקוחות יכולים לקבוע אצלך תורים. שולחים להם את הקישור או שמים אותו בביו באינסטגרם, והעסק יוצא לדרך!"
              : "מורידים את האפליקציה ומתחברים בקליק ומתחילים לנהל את העסק!"}
          </p>

          {businessCode && (
            <>
              <p className={styles.bizLinkHint}>
                {live ? "העמוד שלך:" : "לחיצה כאן תפתח את דף העסק הראשוני שלכם:"}
              </p>
              <a
                href={`https://app.tor.digital/${businessCode}`}
                target="_blank"
                rel="noreferrer"
                className={styles.bizLink}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                </svg>
                <span>app.tor.digital/{businessCode}</span>
              </a>
              {live && (
                <div className={styles.shareRow}>
                  <a
                    className={styles.shareBtn}
                    href={`https://wa.me/?text=${encodeURIComponent(`אפשר לקבוע אצלי תור כאן: ${pageUrl}`)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    שיתוף בוואטסאפ
                  </a>
                  <button type="button" className={styles.shareBtnAlt} onClick={copyLink}>
                    {copied ? "הועתק ✓" : "העתקת הקישור"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ─── Primary CTA — download the app ─── */}
        <div className={styles.primaryCard}>
          <span className={styles.recommendedBadge}>מומלץ!</span>
          <div className={styles.choiceLottie}>
            <Lottie
              animationData={handWithPhoneLottie}
              loop
              autoplay
              style={{ width: "100%", height: "100%" }}
            />
          </div>
          <h3 className={styles.primaryTitle}>מתחברים בקליק עם הטלפון שלך</h3>
          <p className={styles.primaryDesc}>
            ניהול תורים, לקוחות והודעות פוש ישירות מהטלפון
          </p>
          <div className={styles.storeRow}>
            <a
              id="appclick"
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className={styles.storeLink}
            >
              <img src="/assets/appledown.svg" alt="App Store" />
            </a>
            <a
              id="gclick"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className={styles.storeLink}
            >
              <img src="/assets/googledown.svg" alt="Google Play" />
            </a>
          </div>
        </div>

        {/* ─── "או" divider ─── */}
        <div className={styles.orDivider}>
          <span className={styles.orLine} />
          <span className={styles.orText}>או</span>
          <span className={styles.orLine} />
        </div>

        {/* ─── Secondary — web dashboard ─── */}
        <button
          type="button"
          className={styles.dashboardLink}
          onClick={goToDashboard}
          disabled={continuingToDashboard || !authToken}
        >
          {continuingToDashboard ? (
            <span>מעבירים אתכם...</span>
          ) : (
            <>
              <span>המשיכו לדשבורד הניהולי באתר</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </>
          )}
        </button>

        <p className={styles.footnote}>
          אפשר תמיד לעבור בין הפלטפורמות — כל הנתונים מסונכרנים
        </p>
      </div>
    </div>
  );
};

export default Success;
